import { apiGet, apiPost, apiPatch, apiDelete } from '@/utils/api';

export type AcademicEventType =
  | 'PARASCOLAIRE'
  | 'SPECIAL'
  | 'HOLIDAY'
  | 'EXAM_PERIOD'
  | 'VACATION'
  | 'RENTREE'
  | 'OTHER'
  | 'SEMESTER_START'
  | 'SEMESTER_END'
  | 'DELIBERATION'
  | 'ANNUAL_MEETING'
  | 'SEMI_ANNUAL_MEETING'
  | 'EXAM_PREPARATION'
  | 'JURY'
  | 'INSCRIPTION'
  | 'RATTRAPAGE';

export type EventVisibility = 'ALL' | 'STAFF_ONLY' | 'PEDAGOGICAL_ONLY' | 'STUDENTS_AND_STAFF';

export type EventCategoryType = 'SYSTEM' | 'CUSTOM';

// Event Category - allows users to create custom event categories
export interface EventCategory {
  id: string;
  tenantId: string;
  name: string;
  code: string;
  description?: string;
  color: string;
  icon?: string;
  visibility: EventVisibility;
  categoryType: EventCategoryType;
  sortOrder: number;
  isActive: boolean;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateEventCategoryDto {
  name: string;
  code: string;
  description?: string;
  color?: string;
  icon?: string;
  visibility: EventVisibility;
}

export interface UpdateEventCategoryDto {
  name?: string;
  description?: string;
  color?: string;
  icon?: string;
  visibility?: EventVisibility;
  sortOrder?: number;
  isActive?: boolean;
}

// Default system categories with their visibility rules
export const SYSTEM_CATEGORIES: Omit<EventCategory, 'id' | 'tenantId' | 'createdAt' | 'updatedAt'>[] = [
  {
    name: 'Événement Académique',
    code: 'ACADEMIC',
    description: 'Événements liés à la pédagogie: préparation des examens, délibérations, etc.',
    color: '#3B82F6', // Blue
    icon: 'GraduationCap',
    visibility: 'STUDENTS_AND_STAFF',
    categoryType: 'SYSTEM',
    sortOrder: 1,
    isActive: true,
  },
  {
    name: 'Activité Parascolaire',
    code: 'EXTRACURRICULAR',
    description: 'Sorties, activités culturelles, sportives, clubs, etc.',
    color: '#8B5CF6', // Purple
    icon: 'Users',
    visibility: 'ALL',
    categoryType: 'SYSTEM',
    sortOrder: 2,
    isActive: true,
  },
  {
    name: 'Événement Administratif',
    code: 'ADMINISTRATIVE',
    description: 'Réunions internes, formations du personnel, etc.',
    color: '#6B7280', // Gray
    icon: 'Building2',
    visibility: 'STAFF_ONLY',
    categoryType: 'SYSTEM',
    sortOrder: 3,
    isActive: true,
  },
  {
    name: 'Période de Vacances',
    code: 'VACATION',
    description: 'Vacances scolaires, jours fériés, ponts, etc.',
    color: '#10B981', // Green
    icon: 'Calendar',
    visibility: 'ALL',
    categoryType: 'SYSTEM',
    sortOrder: 4,
    isActive: true,
  },
];

// Visibility labels for display
export const VISIBILITY_LABELS: Record<EventVisibility, string> = {
  ALL: 'Tout le monde',
  STAFF_ONLY: 'Personnel uniquement',
  PEDAGOGICAL_ONLY: 'Pédagogie uniquement',
  STUDENTS_AND_STAFF: 'Étudiants et personnel',
};

// Visibility descriptions for tooltips
export const VISIBILITY_DESCRIPTIONS: Record<EventVisibility, string> = {
  ALL: 'Visible par tous: étudiants, parents, professeurs et personnel administratif',
  STAFF_ONLY: 'Visible uniquement par le personnel administratif et les professeurs',
  PEDAGOGICAL_ONLY: 'Visible uniquement par le staff pédagogique (directeurs, coordinateurs)',
  STUDENTS_AND_STAFF: 'Visible par les étudiants et tout le personnel',
};

export interface AcademicEvent {
  id: string;
  tenantId: string;
  academicYearId: string;
  categoryId?: string;
  campusId?: string;
  title: string; // Backend uses 'title', not 'name'
  description?: string;
  eventType: AcademicEventType; // Legacy field
  startDate: string;
  endDate: string;
  isAllDay: boolean;
  isRecurring: boolean;
  color?: string;
  visibility: EventVisibility;
  isAcademic: boolean;
  customLabel?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  academicYear?: {
    id: string;
    name: string;
  };
  campus?: {
    id: string;
    name: string;
  };
  category?: EventCategory;
}

export interface CreateAcademicEventDto {
  academicYearId: string;
  categoryId?: string;
  campusId?: string;
  title: string; // Backend uses 'title', not 'name'
  description?: string;
  eventType: AcademicEventType; // Required by backend
  startDate: string;
  endDate: string;
  isAllDay?: boolean;
  isRecurring?: boolean;
  color?: string;
  visibility?: EventVisibility; // Override category visibility
  isAcademic?: boolean;
  customLabel?: string;
}

/**
 * Update payload. `null` clears a stored value (the backend then falls back
 * to the category colour / visibility); an omitted field is left untouched.
 * The academic year is never sent — the backend rejects unknown fields.
 */
export interface UpdateAcademicEventDto {
  categoryId?: string;
  title?: string;
  description?: string | null;
  eventType?: AcademicEventType;
  startDate?: string;
  endDate?: string;
  isAllDay?: boolean;
  isRecurring?: boolean;
  color?: string | null;
  visibility?: EventVisibility | null;
  campusId?: string | null;
  isAcademic?: boolean;
  customLabel?: string | null;
}

export interface CalendarFilter {
  academicYearId?: string;
  campusId?: string;
  categoryIds?: string[];
  eventTypes?: AcademicEventType[]; // Legacy
  visibility?: EventVisibility;
  startDate?: string;
  endDate?: string;
}

class AnnualPlanningService {
  // ==================== EVENT CATEGORIES ====================

  async getCategories(): Promise<EventCategory[]> {
    return apiGet<EventCategory[]>('/academic-planning/categories');
  }

  async createCategory(data: CreateEventCategoryDto): Promise<EventCategory> {
    return apiPost<EventCategory>('/academic-planning/categories', data);
  }

  async updateCategory(id: string, data: UpdateEventCategoryDto): Promise<EventCategory> {
    return apiPatch<EventCategory>(`/academic-planning/categories/${id}`, data);
  }

  async deleteCategory(id: string): Promise<void> {
    return apiDelete<void>(`/academic-planning/categories/${id}`);
  }

  async initializeSystemCategories(): Promise<EventCategory[]> {
    console.log('Calling initializeSystemCategories...');
    const data = await apiPost<EventCategory[]>('/academic-planning/categories/initialize');
    console.log('System categories initialized successfully:', data);
    return data;
  }

  // ==================== EVENTS ====================

  async getEvents(filter?: CalendarFilter): Promise<AcademicEvent[]> {
    const params = new URLSearchParams();
    if (filter?.academicYearId) params.append('academicYearId', filter.academicYearId);
    if (filter?.campusId) params.append('campusId', filter.campusId);
    if (filter?.categoryIds?.length) params.append('categoryIds', filter.categoryIds.join(','));
    if (filter?.eventTypes?.length) params.append('eventTypes', filter.eventTypes.join(','));
    if (filter?.visibility) params.append('visibility', filter.visibility);
    if (filter?.startDate) params.append('startDate', filter.startDate);
    if (filter?.endDate) params.append('endDate', filter.endDate);

    return apiGet<AcademicEvent[]>(`/academic-planning/events?${params.toString()}`);
  }

  async getEventsForYear(year: number, academicYearId?: string): Promise<AcademicEvent[]> {
    const params = new URLSearchParams();
    params.append('year', year.toString());
    if (academicYearId) params.append('academicYearId', academicYearId);

    return apiGet<AcademicEvent[]>(`/academic-planning/events/year?${params.toString()}`);
  }

  async getCalendarData(academicYearId: string, campusId?: string): Promise<{
    events: AcademicEvent[];
    categories: EventCategory[];
    holidays: Array<{ id: string; name: string; startDate: string; endDate: string }>;
  }> {
    const params = campusId ? `?campusId=${campusId}` : '';
    return apiGet<{
      events: AcademicEvent[];
      categories: EventCategory[];
      holidays: Array<{ id: string; name: string; startDate: string; endDate: string }>;
    }>(`/academic-planning/calendar-data/${academicYearId}${params}`);
  }

  async createEvent(data: CreateAcademicEventDto): Promise<AcademicEvent> {
    return apiPost<AcademicEvent>('/academic-planning/events', data);
  }

  async updateEvent(id: string, data: UpdateAcademicEventDto): Promise<AcademicEvent> {
    return apiPatch<AcademicEvent>(`/academic-planning/events/${id}`, data);
  }

  async deleteEvent(id: string): Promise<void> {
    return apiDelete<void>(`/academic-planning/events/${id}`);
  }

  // ==================== SYNC ENDPOINTS ====================

  async syncHolidaysToEvents(academicYearId: string): Promise<{
    synced: number;
    skipped: number;
    errors: string[];
  }> {
    return apiPost<{ synced: number; skipped: number; errors: string[] }>(
      `/academic-planning/sync/holidays/${academicYearId}`
    );
  }

  async createAcademicYearMarkers(academicYearId: string): Promise<{
    created: number;
    skipped: number;
    errors: string[];
  }> {
    return apiPost<{ created: number; skipped: number; errors: string[] }>(
      `/academic-planning/sync/markers/${academicYearId}`
    );
  }

  async syncAllForAcademicYear(academicYearId: string): Promise<{
    holidays: { synced: number; skipped: number; errors: string[] };
    markers: { created: number; skipped: number; errors: string[] };
  }> {
    return apiPost<{
      holidays: { synced: number; skipped: number; errors: string[] };
      markers: { created: number; skipped: number; errors: string[] };
    }>(`/academic-planning/sync/all/${academicYearId}`);
  }

  // ==================== RESET / MAINTENANCE ENDPOINTS ====================

  async resetAcademicYearEvents(academicYearId: string): Promise<{
    deleted: number;
    markers: { created: number; skipped: number; errors: string[] };
  }> {
    return apiPost<{
      deleted: number;
      markers: { created: number; skipped: number; errors: string[] };
    }>(`/academic-planning/reset/${academicYearId}`);
  }

  async deduplicateHolidays(academicYearId: string): Promise<{
    removed: number;
  }> {
    return apiPost<{ removed: number }>(`/academic-planning/deduplicate/${academicYearId}`);
  }

  async deleteCustomEvents(academicYearId: string): Promise<{
    deleted: number;
  }> {
    return apiDelete<{ deleted: number }>(`/academic-planning/events/custom/${academicYearId}`);
  }
}

export const annualPlanningService = new AnnualPlanningService();

// ==================== HOLIDAY SYNC SERVICE ====================
// This service syncs public holidays to the calendar as events

import { PublicHoliday } from './holidaysService';

export interface HolidaySyncResult {
  synced: number;
  skipped: number;
  errors: string[];
}

class HolidaySyncService {
  /**
   * Convert holidays to calendar events format
   * These pseudo-events are used for display in the calendar views
   */
  convertHolidaysToEvents(
    holidays: PublicHoliday[],
    vacationCategoryId: string,
    academicYearId: string
  ): AcademicEvent[] {
    return holidays.map(holiday => ({
      id: `holiday-${holiday.id}`,
      tenantId: holiday.tenantId,
      academicYearId,
      categoryId: vacationCategoryId,
      campusId: holiday.campusId,
      title: holiday.name,
      description: holiday.nameAr ? `${holiday.nameAr} - Jour férié national` : 'Jour férié national',
      eventType: 'HOLIDAY' as AcademicEventType,
      startDate: holiday.startDate,
      endDate: holiday.endDate,
      isAllDay: true,
      isRecurring: false,
      color: '#F59E0B', // Amber for holidays
      visibility: 'ALL' as EventVisibility,
      isAcademic: false,
      customLabel: holiday.nameAr || undefined,
      createdAt: holiday.startDate,
      updatedAt: holiday.startDate,
      category: {
        id: vacationCategoryId,
        tenantId: holiday.tenantId,
        name: 'Période de Vacances',
        code: 'VACATION',
        color: '#10B981',
        visibility: 'ALL' as EventVisibility,
        categoryType: 'SYSTEM' as EventCategoryType,
        sortOrder: 4,
        isActive: true,
        createdAt: '',
        updatedAt: '',
      },
    }));
  }

  /**
   * Sync holidays to the backend as actual academic events
   */
  async syncHolidaysToEvents(
    holidays: PublicHoliday[],
    academicYearId: string,
    vacationCategoryId: string
  ): Promise<HolidaySyncResult> {
    const result: HolidaySyncResult = {
      synced: 0,
      skipped: 0,
      errors: [],
    };

    for (const holiday of holidays) {
      try {
        // Check if event already exists for this holiday
        const existingEvents = await annualPlanningService.getEvents({
          academicYearId,
          startDate: holiday.startDate.split('T')[0],
          endDate: holiday.endDate.split('T')[0],
        });

        // Skip if we already have an event with this exact title
        const alreadyExists = existingEvents.some(
          event => event.title === holiday.name && event.eventType === 'HOLIDAY'
        );

        if (alreadyExists) {
          result.skipped++;
          continue;
        }

        // Create the event
        await annualPlanningService.createEvent({
          academicYearId,
          categoryId: vacationCategoryId,
          title: holiday.name,
          description: holiday.nameAr ? `${holiday.nameAr} - Jour férié national` : 'Jour férié national',
          eventType: 'HOLIDAY',
          startDate: holiday.startDate.split('T')[0],
          endDate: holiday.endDate.split('T')[0],
          isAllDay: true,
          color: '#F59E0B',
          visibility: 'ALL',
          isAcademic: false,
          customLabel: holiday.nameAr || undefined,
        });

        result.synced++;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        result.errors.push(`Failed to sync ${holiday.name}: ${errorMessage}`);
      }
    }

    return result;
  }
}

export const holidaySyncService = new HolidaySyncService();

// Event type labels for display
export const EVENT_TYPE_LABELS: Record<AcademicEventType, string> = {
  SEMESTER_START: 'Début de semestre',
  SEMESTER_END: 'Fin de semestre',
  HOLIDAY: 'Jour férié',
  VACATION: 'Vacances',
  RENTREE: 'Rentrée',
  EXAM_PREPARATION: 'Préparation des examens',
  EXAM_PERIOD: 'Période des examens',
  DELIBERATION: 'Délibération',
  JURY: 'Jury de délibération',
  ANNUAL_MEETING: 'Réunion annuelle',
  SEMI_ANNUAL_MEETING: 'Réunion semi-annuelle',
  RATTRAPAGE: 'Session de rattrapage',
  INSCRIPTION: "Période d'inscription",
  SPECIAL: 'Événement spécial',
  PARASCOLAIRE: 'Activité parascolaire',
  OTHER: 'Autre',
};

// Event type colors for calendar display
export const EVENT_TYPE_COLORS: Record<AcademicEventType, string> = {
  SEMESTER_START: '#10B981', // green
  SEMESTER_END: '#EF4444', // red
  HOLIDAY: '#F59E0B', // amber
  VACATION: '#3B82F6', // blue
  RENTREE: '#8B5CF6', // purple
  EXAM_PREPARATION: '#EC4899', // pink
  EXAM_PERIOD: '#F97316', // orange
  DELIBERATION: '#06B6D4', // cyan
  JURY: '#6366F1', // indigo
  ANNUAL_MEETING: '#14B8A6', // teal
  SEMI_ANNUAL_MEETING: '#84CC16', // lime
  RATTRAPAGE: '#EAB308', // yellow
  INSCRIPTION: '#0EA5E9', // sky
  SPECIAL: '#D946EF', // fuchsia
  PARASCOLAIRE: '#A855F7', // violet
  OTHER: '#6B7280', // gray
};
